import { type MenuOption } from '@saviynt/design-system';

import { FETCH_OPERATORS, FORM_IP_MAP } from './constants';
import {
  type File,
  type FormData,
  type FormFieldState,
  type InputValueType,
  type TrackedFormData,
  type TrackedFormFieldState,
} from './types';

export const DEFAULT_TRACKER_CONFIG = { isCritical: false };

export const addTracker = (sections: FormData[]): TrackedFormData[] =>
  sections.map((section) => ({
    ...section,
    fields: section.fields?.map((field) => ({
      ...field,
      tracker: DEFAULT_TRACKER_CONFIG,
    })),
    sections: section.sections ? addTracker(section.sections) : undefined,
  }));

const regexPattern = /<\/?[a-z][\s\S]*?>/i;

const isValidFile = (files: File[] | undefined) =>
  Boolean(files && files.length > 0);

const isValidValue = (value: InputValueType | undefined) =>
  Boolean(
    value !== undefined &&
      value !== null &&
      String(value).trim() !== '' &&
      !regexPattern.test(String(value))
  );

const isValidOption = (options: MenuOption[] | undefined) =>
  Boolean(options && options.length > 0 && options.some((o) => o.isChecked));

const validateField = (field: TrackedFormFieldState): boolean => {
  const isValueBased = Boolean(
    field.type === FORM_IP_MAP.INPUT ||
      field.type === FORM_IP_MAP.TEXTAREA ||
      field.type === FORM_IP_MAP.DATE
  );
  const isOptionsBased = Boolean(
    field.type === FORM_IP_MAP.RADIO ||
      field.type === FORM_IP_MAP.SELECT ||
      field.type === FORM_IP_MAP.CHECKBOX
  );
  const isFile = Boolean(field.type === FORM_IP_MAP.UPLOAD);

  if (field.isRequired && isValueBased && !isValidValue(field.value)) {
    return false;
  }

  if (field.isRequired && isOptionsBased && !isValidOption(field.options)) {
    return false;
  }

  if (field.isRequired && isFile && !isValidFile(field.files)) {
    return false;
  }

  return true;
};

const validateFormData = (form: TrackedFormData): boolean => {
  if (form.fields && !form.fields.every(validateField)) {
    return false;
  }

  if (form.sections && !form.sections.every(validateFormData)) {
    return false;
  }

  return true;
};

export const validateForm = (forms: TrackedFormData[]): boolean =>
  forms.every(validateFormData);

export const processTracker = (
  trackedFormData: TrackedFormData[]
): TrackedFormData[] =>
  trackedFormData.map((section) => {
    const currentSection = { ...section };
    const updatedFields = section.fields?.map((field) => {
      const isValueBased = Boolean(
        field.type === FORM_IP_MAP.INPUT ||
          field.type === FORM_IP_MAP.TEXTAREA ||
          field.type === FORM_IP_MAP.DATE
      );
      const isOptionsBased = Boolean(
        field.type === FORM_IP_MAP.RADIO ||
          field.type === FORM_IP_MAP.SELECT ||
          field.type === FORM_IP_MAP.CHECKBOX
      );
      const isFileBased = Boolean(field.type === FORM_IP_MAP.UPLOAD);

      if (isValueBased) {
        return {
          ...field,
          tracker: {
            ...field.tracker,
            isCritical: Boolean(field.isRequired) && !isValidValue(field.value),
          },
        };
      }

      if (isOptionsBased) {
        return {
          ...field,
          tracker: {
            ...field.tracker,
            isCritical:
              Boolean(field.isRequired) && !isValidOption(field.options),
          },
        };
      }

      if (isFileBased) {
        return {
          ...field,
          tracker: {
            ...field.tracker,
            isCritical: Boolean(field.isRequired) && !isValidFile(field.files),
          },
        };
      }

      return field;
    });

    const updatedSubSections = section.sections
      ? processTracker(section.sections)
      : undefined;

    if (updatedFields) {
      currentSection.fields = updatedFields;
    }

    if (updatedSubSections) {
      currentSection.sections = updatedSubSections;
    }

    return currentSection;
  });

interface UpdateFormConfig {
  value?: InputValueType;
  options?: MenuOption[];
  files?: File[];
  isNewOption?: boolean;
  newFields?: TrackedFormFieldState[];
}

export const updateForm = (
  name: string,
  type: string,
  trackedFormData: TrackedFormData[],
  { value, options, files, newFields, isNewOption }: UpdateFormConfig
) =>
  trackedFormData.map((section) => {
    const currentSection = { ...section };
    const updatedFields = section.fields?.map((field) => {
      const isField = Boolean(field.name === name && field.type === type);
      const isValueBased = Boolean(
        type === FORM_IP_MAP.INPUT ||
          type === FORM_IP_MAP.TEXTAREA ||
          type === FORM_IP_MAP.DATE
      );
      const isOptionsBased = Boolean(
        type === FORM_IP_MAP.RADIO ||
          type === FORM_IP_MAP.SELECT ||
          type === FORM_IP_MAP.CHECKBOX
      );
      const isFileBased = Boolean(type === FORM_IP_MAP.UPLOAD);

      if (isField && isValueBased) {
        return {
          ...field,
          value,
          tracker: {
            ...field.tracker,
            isCritical: Boolean(field.isRequired) && !isValidValue(value),
          },
        };
      }

      if (isField && isOptionsBased) {
        return {
          ...field,
          options,
          tracker: {
            ...field.tracker,
            isCritical:
              Boolean(field.isRequired) &&
              !isNewOption &&
              !isValidOption(options),
          },
        };
      }

      if (isField && isFileBased) {
        return {
          ...field,
          files,
          tracker: {
            ...field.tracker,
            isCritical: Boolean(field.isRequired) && !isValidFile(files),
          },
        };
      }

      return field;
    });

    const isFieldAvailable = section.fields?.find((f) => f.name === name);

    if (isFieldAvailable && newFields && newFields.length > 0) {
      updatedFields?.push(...newFields);
    }

    const updatedSubSections = section.sections
      ? updateForm(name, type, section.sections, {
          value,
          options,
          files,
          newFields,
        })
      : undefined;

    if (updatedFields) {
      currentSection.fields = updatedFields;
    }

    if (updatedSubSections) {
      currentSection.sections = updatedSubSections;
    }

    return currentSection;
  });

export const removeTracker = (trackedFormData: TrackedFormData[]): FormData[] =>
  trackedFormData.map((section) => {
    const currentSection: FormData = { ...section };
    const updatedFields = section.fields?.map((field) => {
      const { tracker, ...rest } = field;

      return rest;
    });

    const updatedSubSections = section.sections
      ? removeTracker(section.sections)
      : undefined;

    if (updatedFields) {
      currentSection.fields = updatedFields;
    }

    if (updatedSubSections) {
      currentSection.sections = updatedSubSections;
    }

    return currentSection;
  });

export const updateEndpointData = (
  name: string,
  finalvalue: string,
  trackedFormData: TrackedFormData[]
): TrackedFormData[] =>
  trackedFormData.map((section) => {
    const currentSection = { ...section };
    const updatedFields = section.fields?.map((field) => {
      const isValueBased = Boolean(
        field.type === FORM_IP_MAP.INPUT ||
          field.type === FORM_IP_MAP.TEXTAREA ||
          field.type === FORM_IP_MAP.DATE
      );

      if (
        isValueBased &&
        field.endpoint?.payload &&
        name in field.endpoint.payload
      ) {
        return {
          ...field,
          value: null,
          endpoint: {
            ...field.endpoint,
            payload: {
              ...field.endpoint.payload,
              [name]: finalvalue,
            },
          },
        };
      }

      if (
        isValueBased &&
        field.endpoint?.queryParams &&
        name in field.endpoint.queryParams
      ) {
        return {
          ...field,
          value: null,
          endpoint: {
            ...field.endpoint,
            queryParams: {
              ...field.endpoint.queryParams,
              [name]: finalvalue,
            },
          },
        };
      }

      return field;
    });

    const updatedSubSections = section.sections
      ? updateEndpointData(name, finalvalue, section.sections)
      : undefined;

    if (updatedFields) {
      currentSection.fields = updatedFields;
    }

    if (updatedSubSections) {
      currentSection.sections = updatedSubSections;
    }

    return currentSection;
  });

export const validateOptions = (
  operator: string,
  currentOptions: InputValueType[],
  conditionalOptions?: InputValueType[]
): boolean => {
  switch (operator) {
    case FETCH_OPERATORS.EQUALS:
      return (
        currentOptions.length === (conditionalOptions?.length ?? 0) &&
        currentOptions.every((v) => conditionalOptions?.includes(v))
      );

    case FETCH_OPERATORS.NOT_EQUALS:
      return !(
        currentOptions.length === (conditionalOptions?.length ?? 0) &&
        currentOptions.every((v) => conditionalOptions?.includes(v))
      );

    case FETCH_OPERATORS.IN:
      return currentOptions.some((v) => conditionalOptions?.includes(v));

    case FETCH_OPERATORS.NOT_IN:
      return currentOptions.every((v) => !conditionalOptions?.includes(v));

    case FETCH_OPERATORS.EXISTS:
      return currentOptions.length > 0;

    case FETCH_OPERATORS.NOT_EXISTS:
      return currentOptions.length === 0;

    default:
      return false;
  }
};

export const validateValues = (
  operator: string,
  currentValue: InputValueType,
  conditionalValue?: InputValueType
): boolean => {
  switch (operator) {
    case FETCH_OPERATORS.EQUALS:
      return currentValue === conditionalValue;

    case FETCH_OPERATORS.NOT_EQUALS:
      return currentValue !== conditionalValue;

    case FETCH_OPERATORS.IN:
      return (
        typeof currentValue === 'string' &&
        currentValue.includes(String(conditionalValue))
      );

    case FETCH_OPERATORS.NOT_IN:
      return !(
        typeof currentValue === 'string' &&
        currentValue.includes(String(conditionalValue))
      );

    case FETCH_OPERATORS.EXISTS:
      return (
        currentValue !== null &&
        !(typeof currentValue === 'string' && currentValue.trim().length === 0)
      );

    case FETCH_OPERATORS.NOT_EXISTS:
      return (
        currentValue === null ||
        (typeof currentValue === 'string' && currentValue.trim().length === 0)
      );

    default:
      return false;
  }
};

export const blobToBase64 = (blob: Blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export const convertNestedToLinear = (formData: FormData[]) => {
  const temp: FormFieldState[] = [];

  formData.forEach((formObj) => {
    if (formObj.fields && formObj.fields.length > 0) {
      temp.push(...formObj.fields);
    }

    if (formObj.sections) {
      temp.push(...convertNestedToLinear(formObj.sections));
    }
  });

  return temp;
};

export const sanitizeFields = (fields: FormFieldState[]) =>
  fields.map((field) => {
    const base = {
      label: field.label,
      name: field.name,
      type: field.type,
      isEditable: field.isEditable,
      isRequired: field.isRequired,
    };
    const isValueBased = Boolean(
      field.type === FORM_IP_MAP.INPUT ||
        field.type === FORM_IP_MAP.TEXTAREA ||
        field.type === FORM_IP_MAP.DATE
    );
    const isOptionsBased = Boolean(
      field.type === FORM_IP_MAP.RADIO ||
        field.type === FORM_IP_MAP.SELECT ||
        field.type === FORM_IP_MAP.CHECKBOX
    );
    const isFileBased = Boolean(field.type === FORM_IP_MAP.UPLOAD);

    if (isValueBased) {
      return {
        ...base,
        value: field.value ?? null,
      };
    }

    if (isOptionsBased) {
      return {
        ...base,
        options: field.options?.filter((o) => o.isChecked) ?? [],
      };
    }

    if (isFileBased) {
      return {
        ...base,
        files: field.files ?? [],
      };
    }

    return field;
  });

export const getFilledForm = (
  formData: TrackedFormData[],
  formFields: FormFieldState[]
) => {
  const fieldMap = new Map(formFields.map((field) => [field.name, field]));

  const fillField = (field: TrackedFormFieldState): TrackedFormFieldState => {
    const defaultField = fieldMap.get(field.name);

    if (!defaultField) {
      return field;
    }

    const isValueBased = Boolean(
      field.type === FORM_IP_MAP.INPUT ||
        field.type === FORM_IP_MAP.TEXTAREA ||
        field.type === FORM_IP_MAP.DATE
    );
    const isOptionsBased = Boolean(
      field.type === FORM_IP_MAP.RADIO ||
        field.type === FORM_IP_MAP.SELECT ||
        field.type === FORM_IP_MAP.CHECKBOX
    );
    const isFileBased = Boolean(field.type === FORM_IP_MAP.UPLOAD);

    if (isValueBased) {
      return { ...field, value: defaultField.value ?? field.value };
    }

    if (isOptionsBased) {
      return {
        ...field,
        options: field.options?.map((option) => ({
          ...option,
          isChecked:
            defaultField.options?.some(
              (o) => o.value === option.value && o.isChecked
            ) ?? option.isChecked,
        })),
      };
    }

    if (isFileBased) {
      return { ...field, files: defaultField.files ?? field.files };
    }

    return field;
  };

  const fillSection = (section: TrackedFormData): TrackedFormData => ({
    ...section,
    fields: section.fields?.map(fillField),
    sections: section.sections?.map(fillSection),
  });

  return formData.map(fillSection);
};

export const countFields = (trackedFormData: TrackedFormData[]): number => {
  let count = 0;

  trackedFormData.forEach((section) => {
    if (section.fields) {
      count += section.fields.length;
    }

    if (section.sections) {
      count += countFields(section.sections);
    }
  });

  return count;
};
